/**
 * Хуки данных модуля «Анонсы» (мероприятия отеля) — Tanstack Query.
 *
 * Менеджер/владелец: GET /v1/events (все состояния) + мутации
 * create/update/publish/hide/delete (internal/events/http_app, RBAC
 * hotel_manager|platform_admin). Гость/персонал: тот же GET /v1/events,
 * бэк по JWT-роли отдаёт только published. Server-state только через
 * React Query; все мутации инвалидируют список.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  cancelRsvp,
  createEvent,
  createRsvp,
  createScheduleItem,
  deleteEvent,
  deleteScheduleItem,
  getRsvpStatus,
  hideEvent,
  listEvents,
  listSchedule,
  publishEvent,
  updateEvent,
} from '@hsc/api-client';
import type {
  Event,
  EventCreateInput,
  EventListResponse,
  EventUpdateInput,
  RsvpStatus,
  ScheduleItem,
  ScheduleItemInput,
  ScheduleListResponse,
} from '@hsc/types';

const EVENTS_KEY = ['events', 'list'] as const;
const RSVP_KEY = ['events', 'rsvp'] as const;
const SCHEDULE_KEY = ['events', 'schedule'] as const;

/** Список событий (роль-зависимо фильтруется бэком). */
export function useEvents() {
  return useQuery<EventListResponse>({
    queryKey: EVENTS_KEY,
    queryFn: () => listEvents(),
    staleTime: 30_000,
  });
}

/** Создание черновика события. */
export function useCreateEvent() {
  const qc = useQueryClient();
  return useMutation<Event, Error, EventCreateInput>({
    mutationFn: (input) => createEvent(input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['events'] }),
  });
}

/** Обновление полей события (название/описание/даты). */
export function useUpdateEvent() {
  const qc = useQueryClient();
  return useMutation<Event, Error, { id: string; input: EventUpdateInput }>({
    mutationFn: ({ id, input }) => updateEvent(id, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['events'] }),
  });
}

/** Действия жизненного цикла: публикация / скрытие / удаление. */
export function useEventLifecycle() {
  const qc = useQueryClient();
  return useMutation<
    Event | void,
    Error,
    { id: string; action: 'publish' | 'hide' | 'delete'; reason?: string }
  >({
    mutationFn: ({ id, action, reason }) => {
      switch (action) {
        case 'publish':
          return publishEvent(id);
        case 'hide':
          return hideEvent(id, { reason: reason ?? '' });
        case 'delete':
          return deleteEvent(id);
      }
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['events'] }),
  });
}

// --- RSVP (Волна 5 / A3 «Облака») — гостевая запись на мероприятие ---------

/**
 * Статус записи гостя на событие + счётчики мест. Включается только при
 * наличии id (для раскрытой карточки события у гостя).
 */
export function useRsvpStatus(eventId: string | undefined) {
  return useQuery<RsvpStatus>({
    queryKey: [...RSVP_KEY, eventId],
    queryFn: () => getRsvpStatus(eventId as string),
    enabled: eventId !== undefined && eventId !== '',
    staleTime: 15_000,
  });
}

/**
 * Запись / отмена записи гостя на событие. Ответ ручки (актуальный статус +
 * счётчики) сразу пишем в кэш статуса — UI обновляется без гонки рефетча;
 * затем инвалидируем для фоновой сверки.
 */
export function useToggleRsvp() {
  const qc = useQueryClient();
  return useMutation<RsvpStatus, Error, { id: string; join: boolean }>({
    mutationFn: ({ id, join }) => (join ? createRsvp(id) : cancelRsvp(id)),
    onSuccess: (data, { id }) => {
      qc.setQueryData<RsvpStatus>([...RSVP_KEY, id], data);
      void qc.invalidateQueries({ queryKey: [...RSVP_KEY, id] });
    },
  });
}

// --- Расписание дня (миграция 0066) — «время + текст», отдельно от событий ---

/** Список строк расписания дня (authed; гость читает, менеджер правит). */
export function useSchedule() {
  return useQuery<ScheduleListResponse>({
    queryKey: SCHEDULE_KEY,
    queryFn: () => listSchedule(),
    staleTime: 30_000,
  });
}

/** Добавить строку расписания (manager). */
export function useCreateScheduleItem() {
  const qc = useQueryClient();
  return useMutation<ScheduleItem, Error, ScheduleItemInput>({
    mutationFn: (input) => createScheduleItem(input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: SCHEDULE_KEY }),
  });
}

/** Удалить строку расписания (manager). */
export function useDeleteScheduleItem() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => deleteScheduleItem(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: SCHEDULE_KEY }),
  });
}
